<script setup lang="ts">
/**
 * Заметка в совместной правке — макет 10-3 (332:17424).
 *
 * Текст держит Yjs, поле — обычная textarea: контент-editable дал бы каретки
 * даром, но взамен принёс бы вставку разметки, свои переносы строк и войну с
 * IME на вьетнамской раскладке.
 *
 * Каретки и выделения соседей рисует зеркало — слой поверх поля с тем же
 * текстом, набранным теми же метриками. Текст в нём прозрачный, видны только
 * метки: другого способа показать чужой курсор в textarea нет.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { applyTextDiff, commonPrefix, peerColor, type NoteDoc, type PeerState } from '../collab'
import { useMeetingStore } from '../store'

const props = defineProps<{
  noteId: string
  /** Текст заметки: с него начинается документ, если её ещё не правили вместе */
  body: string
  /** Кто правит эту же заметку прямо сейчас */
  peers: PeerState[]
}>()

const emit = defineEmits<{ close: [] }>()

const meetings = useMeetingStore()

const field = ref<HTMLTextAreaElement | null>(null)
const value = ref(props.body)

let doc: NoteDoc | null = null

onMounted(async () => {
  doc = await meetings.openNote(props.noteId, props.body)
  value.value = doc.text.toString()

  meetings.setPresence({ noteId: props.noteId, from: 0, to: 0, typing: false })

  await nextTick()
  field.value?.focus()
})

onBeforeUnmount(() => {
  meetings.setPresence({ noteId: '', from: 0, to: 0, typing: false })
  void meetings.closeNote(props.noteId)
})

/**
 * Чужая правка приехала — переносим её в поле, не теряя своей каретки.
 *
 * Курсор сдвигается ровно настолько, насколько изменился текст до него: без
 * этого сосед, пишущий выше, каждым словом уносил бы твою каретку в начало
 * строки.
 */
watch(
  () => meetings.revision,
  async () => {
    const next = doc?.text.toString() ?? ''

    if (next === value.value) return

    const element = field.value
    const caret = element?.selectionStart ?? 0
    const shift = next.length - value.value.length
    const changedBefore = commonPrefix(next, value.value) < caret

    value.value = next

    if (!element) return

    await nextTick()

    const moved = changedBefore ? Math.max(0, caret + shift) : caret

    element.setSelectionRange(moved, moved)
  },
)

/** Своя правка уходит в документ куском — что именно изменилось, считает collab */
const onInput = () => {
  if (!doc) return

  applyTextDiff(doc.text, value.value)
  reportCaret()
}

/** Где стоит наша каретка: соседи рисуют её у себя */
const reportCaret = () => {
  const element = field.value

  if (!element) return

  meetings.setPresence({
    noteId: props.noteId,
    from: element.selectionStart,
    to: element.selectionEnd,
    typing: false,
  })
}

/**
 * Кого рисуем в зеркале.
 *
 * ponytail: одну каретку за раз — ту, что пришла первой. Втроём в одну заметку
 * пишут редко, а слоёв разметки на каждого соседа зеркалу пришлось бы столько
 * же, сколько соседей.
 */
const peer = computed(() => props.peers[0] ?? null)

const head = computed(() =>
  value.value.slice(0, Math.min(peer.value?.from ?? 0, value.value.length)),
)

const selected = computed(() => {
  const from = Math.min(peer.value?.from ?? 0, value.value.length)
  const to = Math.min(peer.value?.to ?? 0, value.value.length)

  return value.value.slice(Math.min(from, to), Math.max(from, to))
})

const tail = computed(() => {
  const to = Math.max(peer.value?.from ?? 0, peer.value?.to ?? 0)

  return value.value.slice(Math.min(to, value.value.length))
})

const color = computed(() => (peer.value ? peerColor(peer.value.userId) : 'transparent'))
</script>

<template>
  <div class="live">
    <!-- Зеркало: тот же текст теми же метриками, видны только чужие метки. -->
    <div v-if="peer" class="live__mirror" aria-hidden="true">
      <span>{{ head }}</span>
      <span class="live__selection" :style="{ background: color }">{{ selected }}</span>
      <span class="live__caret" :style="{ background: color }">
        <span class="live__name" :style="{ background: color }">{{ peer.name }}</span>
      </span>
      <span>{{ tail }}</span>
    </div>

    <textarea
      ref="field"
      v-model="value"
      class="live__input"
      rows="3"
      @input="onInput"
      @keyup="reportCaret"
      @click="reportCaret"
      @select="reportCaret"
      @keydown.esc.stop="emit('close')"
    />
  </div>
</template>

<style scoped>
.live {
  position: relative;
}

/* Метрики зеркала и поля обязаны совпадать до пикселя: разойдись они —
   каретка соседа встанет не на свою букву. */
.live__mirror,
.live__input {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid transparent;
  font-family: inherit;
  font-size: var(--font-size-body);
  line-height: 20px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.live__mirror {
  position: absolute;
  inset: 0;
  color: transparent;
  pointer-events: none;
}

.live__selection {
  border-radius: 2px;
  /* Выделение соседа лежит поверх текста: сплошная заливка спрятала бы его. */
  opacity: 0.22;
}

.live__caret {
  position: relative;
  display: inline-block;
  width: 2px;
  height: 18px;
  vertical-align: text-bottom;
  border-radius: 1px;
}

.live__name {
  position: absolute;
  top: -15px;
  left: -1px;
  padding: 0 4px;
  border-radius: 4px;
  color: #fff;
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-medium);
  line-height: 14px;
  white-space: nowrap;
}

.live__input {
  position: relative;
  border-color: var(--color-primary);
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text-primary);
  resize: none;
}

.live__input:focus {
  outline: none;
}
</style>
